/*
 * Conversation persistence using OfficeRuntime.storage.
 * Provides CRUD operations for conversations with an index structure.
 */

/* global OfficeRuntime */

const INDEX_KEY = "sheetai.conversations.index";
const CONV_PREFIX = "sheetai.conv.";

/**
 * Generate a unique ID for a conversation.
 * @returns {string} Unique ID
 */
function generateId() {
  return "conv_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
}

/**
 * Get the conversation index from storage.
 * @returns {Promise<Object>} Index object with conversations array
 */
export async function getIndex() {
  try {
    const raw = await OfficeRuntime.storage.getItem(INDEX_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error("Error loading conversation index:", e);
  }
  return { conversations: [], activeConversationId: null };
}

/**
 * Save the conversation index to storage.
 * @param {Object} index - Index object to save
 */
export async function saveIndex(index) {
  try {
    await OfficeRuntime.storage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch (e) {
    console.error("Error saving conversation index:", e);
    throw e;
  }
}

/**
 * Load a conversation from storage by ID.
 * @param {string} id - Conversation ID
 * @returns {Promise<Object|null>} Conversation object or null
 */
export async function loadConversation(id) {
  try {
    const raw = await OfficeRuntime.storage.getItem(CONV_PREFIX + id);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error("Error loading conversation:", e);
  }
  return null;
}

/**
 * Save a conversation to storage.
 * @param {Object} conv - Conversation object to save
 */
export async function saveConversation(conv) {
  try {
    await OfficeRuntime.storage.setItem(CONV_PREFIX + conv.id, JSON.stringify(conv));
  } catch (e) {
    console.error("Error saving conversation:", e);
    throw e;
  }
}

/**
 * Delete a conversation from storage.
 * @param {string} id - Conversation ID to delete
 */
export async function deleteConversation(id) {
  try {
    await OfficeRuntime.storage.removeItem(CONV_PREFIX + id);
  } catch (e) {
    console.error("Error deleting conversation:", e);
    throw e;
  }
}

/**
 * List all conversations from the index.
 * @returns {Promise<Array>} Array of conversation metadata
 */
export async function listConversations() {
  const index = await getIndex();
  return index.conversations || [];
}

/**
 * Create a new conversation.
 * @param {string} name - Optional name (defaults to auto-generated)
 * @param {string} firstMessage - Optional first message to auto-generate name
 * @returns {Promise<Object>} Created conversation object
 */
export async function createConversation(name, firstMessage) {
  const id = generateId();
  const now = new Date().toISOString();

  // Auto-generate name from first message if not provided
  const convName = name || (firstMessage ? firstMessage.substring(0, 40) : "Conversation");

  const conv = {
    id,
    name: convName,
    createdAt: now,
    updatedAt: now,
    messages: [],
    messageCount: 0,
  };

  await saveConversation(conv);

  // Update index
  const index = await getIndex();
  index.conversations.push({
    id,
    name: convName,
    createdAt: now,
    updatedAt: now,
    messageCount: 0,
  });
  index.activeConversationId = id;
  await saveIndex(index);

  return conv;
}

/**
 * Set the active conversation ID.
 * @param {string} id - Conversation ID to set as active
 */
export async function setActiveConversation(id) {
  const index = await getIndex();
  index.activeConversationId = id;
  await saveIndex(index);
}

/**
 * Get the active conversation ID.
 * @returns {Promise<string|null>} Active conversation ID
 */
export async function getActiveConversationId() {
  const index = await getIndex();
  return index.activeConversationId;
}

/**
 * Rename a conversation.
 * @param {string} id - Conversation ID
 * @param {string} name - New name
 */
export async function renameConversation(id, name) {
  const index = await getIndex();
  const convMeta = index.conversations.find((c) => c.id === id);
  if (convMeta) {
    convMeta.name = name;
    convMeta.updatedAt = new Date().toISOString();
    await saveIndex(index);

    // Also update the conversation object
    const conv = await loadConversation(id);
    if (conv) {
      conv.name = name;
      conv.updatedAt = convMeta.updatedAt;
      await saveConversation(conv);
    }
  }
}

/**
 * Delete a conversation and remove from index.
 * @param {string} id - Conversation ID to delete
 */
export async function removeConversation(id) {
  await deleteConversation(id);

  const index = await getIndex();
  index.conversations = index.conversations.filter((c) => c.id !== id);
  if (index.activeConversationId === id) {
    index.activeConversationId = index.conversations.length > 0 ? index.conversations[0].id : null;
  }
  await saveIndex(index);
}

/**
 * Update conversation metadata in index after adding messages.
 * @param {Object} conv - Conversation object with updated messages
 */
export async function updateConversationMeta(conv) {
  const index = await getIndex();
  const convMeta = index.conversations.find((c) => c.id === conv.id);
  if (convMeta) {
    convMeta.messageCount = conv.messages.length;
    convMeta.updatedAt = new Date().toISOString();

    // Auto-generate name from first user message if still default
    if (convMeta.name === "Conversation" && conv.messages.length > 0) {
      const firstUserMsg = conv.messages.find((m) => m.role === "user");
      if (firstUserMsg && firstUserMsg.content) {
        convMeta.name = firstUserMsg.content.substring(0, 40);
        conv.name = convMeta.name;
      }
    }

    await saveIndex(index);
    await saveConversation(conv);
  }
}

/**
 * Get storage usage info.
 * @returns {Promise<Object>} Storage usage statistics
 */
export async function getStorageUsage() {
  try {
    const keys = await OfficeRuntime.storage.getKeys();
    const convKeys = keys.filter((k) => k.startsWith(CONV_PREFIX));
    let totalSize = 0;

    // Estimate size (this is approximate)
    for (const key of convKeys) {
      const value = await OfficeRuntime.storage.getItem(key);
      if (value) {
        totalSize += key.length + value.length;
      }
    }

    // Add index size
    const indexValue = await OfficeRuntime.storage.getItem(INDEX_KEY);
    if (indexValue) {
      totalSize += INDEX_KEY.length + indexValue.length;
    }

    return {
      conversationCount: convKeys.length,
      estimatedSizeKB: Math.round(totalSize / 1024),
      limitMB: 10, // OfficeRuntime.storage limit
      usedPercent: Math.round((totalSize / (10 * 1024 * 1024)) * 100),
    };
  } catch (e) {
    console.error("Error getting storage usage:", e);
    return { conversationCount: 0, estimatedSizeKB: 0, limitMB: 10, usedPercent: 0 };
  }
}
